import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Building2, IndianRupee, TrendingUp, CheckCircle2, XCircle, ReceiptText,
} from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  approved: "bg-brand-green/10 text-brand-green border-brand-green/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  active: "bg-brand-green/10 text-brand-green border-brand-green/20",
  expired: "bg-muted",
};

export default function AdminFinance() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [subs, setSubs] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, count: 0, recent: [] });
  const [transactions, setTransactions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("approve");
  const [note, setNote] = useState("");

  const load = async () => {
    const [w, s, e, t] = await Promise.all([
      api.get("/admin/withdrawals"),
      api.get("/admin/subscriptions"),
      api.get("/admin/platform-earnings"),
      api.get("/admin/transactions?limit=100"),
    ]);
    setWithdrawals(w.data);
    setSubs(s.data);
    setEarnings(e.data);
    setTransactions(t.data);
  };
  useEffect(() => { load(); }, []);

  const money = (v) => "₹" + Number(v || 0).toFixed(2);

  const doAction = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/withdrawals/${selected.id}/action`, { action, note });
      toast.success(`Withdrawal ${action}d`);
      setSelected(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Platform ops</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Finance & Compliance</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
            <TrendingUp size={14} /> Platform earnings
          </div>
          <p className="mt-2 text-3xl font-display font-semibold tabular-nums text-brand-green" data-testid="platform-total">
            {money(earnings.total)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">4% of {earnings.count} walk-in orders</p>
        </Card>
        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
            <IndianRupee size={14} /> Pending withdrawals
          </div>
          <p className="mt-2 text-3xl font-display font-semibold tabular-nums">
            {withdrawals.filter((w) => w.status === "pending").length}
          </p>
        </Card>
        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
            <Building2 size={14} /> Active subs
          </div>
          <p className="mt-2 text-3xl font-display font-semibold tabular-nums">
            {subs.filter((s) => s.status === "active").length}
          </p>
        </Card>
        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
            <ReceiptText size={14} /> Transactions
          </div>
          <p className="mt-2 text-3xl font-display font-semibold tabular-nums">{transactions.length}</p>
        </Card>
      </div>

      <Tabs defaultValue="withdrawals">
        <TabsList>
          <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">Platform earnings</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-tx">All transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="withdrawals">
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>UPI</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withdrawals.map((w) => (
                  <TableRow key={w.id} data-testid={`wd-row-${w.id}`}>
                    <TableCell className="text-sm">{w.user_name || w.user_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {w.bank_account_name}<br />
                      A/C: {w.bank_account_number} · {w.bank_ifsc}
                    </TableCell>
                    <TableCell className="text-xs">{w.upi_id || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{money(w.amount)}</TableCell>
                    <TableCell className="text-xs">{new Date(w.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLES[w.status]}>{w.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {w.status === "pending" && (
                        <div className="flex gap-1">
                          <Button size="sm" className="bg-brand-green hover:bg-brand-green-hover text-white h-8" onClick={() => { setSelected(w); setAction("approve"); }} data-testid={`wd-approve-${w.id}`}>
                            <CheckCircle2 size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => { setSelected(w); setAction("reject"); }} data-testid={`wd-reject-${w.id}`}>
                            <XCircle size={14} />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {withdrawals.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No withdrawal requests yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="subscriptions">
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Activated</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {subs.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm">{s.user_id.slice(0, 8)}</TableCell>
                    <TableCell className="capitalize font-medium">{s.plan}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(s.amount)}</TableCell>
                    <TableCell className="text-xs">{s.activated_at ? new Date(s.activated_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-xs">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLES[s.status] || ""}>{s.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {subs.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No subscriptions yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="earnings">
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Retailer</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Commission %</TableHead>
                <TableHead className="text-right">Platform earning</TableHead>
                <TableHead>When</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {earnings.recent.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono text-xs">{e.order_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs">{e.retailer_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(e.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{e.commission_pct}%</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-brand-green">{money(e.amount)}</TableCell>
                    <TableCell className="text-xs">{new Date(e.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {earnings.recent.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No walk-in payments yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>User</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs">{t.user_id.slice(0, 8)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={t.type === "credit" ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "text-destructive border-destructive/20"}>
                        {t.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-[380px] truncate">{t.description}</TableCell>
                    <TableCell className="text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${t.type === "credit" ? "text-brand-green" : ""}`}>
                      {t.type === "credit" ? "+" : "−"}{money(t.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">{action} withdrawal</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="text-sm space-y-1">
                <p><span className="text-muted-foreground">Retailer:</span> {selected.user_name}</p>
                <p><span className="text-muted-foreground">Amount:</span> <b>{money(selected.amount)}</b></p>
                <p><span className="text-muted-foreground">Bank:</span> {selected.bank_account_number} · {selected.bank_ifsc}</p>
                {selected.upi_id && <p><span className="text-muted-foreground">UPI:</span> {selected.upi_id}</p>}
              </div>
              <Textarea placeholder={action === "reject" ? "Reason (required)" : "Payment reference / note (optional)"}
                value={note} onChange={(e) => setNote(e.target.value)} data-testid="wd-note-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button
              onClick={doAction}
              className={action === "approve" ? "bg-brand-green hover:bg-brand-green-hover" : "bg-destructive hover:bg-destructive/90"}
              data-testid="wd-confirm-btn"
            >
              Confirm {action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
